import type {SketchDimensionSnapshot} from './SketchDimensionTypes';

/**
 * 计算链尺寸组的共线标注位置（纯函数，拖动预览与事务提交共用）。
 *
 * SW 语义：链尺寸组的所有尺寸线共线——同在一条平行于链方向的轴上；
 * 拖动任意一条标注时，整组沿链法向联动到新轴、保持共线。
 * 被拖条完全跟随目标位置（沿链方向也可滑动），其余成员只把**法向分量**
 * 对齐到被拖条所在的轴、各自沿链方向的位置不变。
 *
 * @param members 同一链组的全部尺寸快照。
 * @param draggedDimensionId 被拖尺寸持久 ID。
 * @param draggedLabel 被拖标注的目标位置（草图局部）。
 * @returns 尺寸 ID → 新标注位置；链方向缺失（数据异常）时返回 null 表示不联动。
 */
export function resolveChainAlignedLabelPositions(
    members: readonly SketchDimensionSnapshot[],
    draggedDimensionId: string,
    draggedLabel: readonly [number, number],
): ReadonlyMap<string, readonly [number, number]> | null {
    const dragged = members.find((snapshot) => snapshot.dimensionId === draggedDimensionId);
    /** 链方向：fixedDirection（链尺寸）或 orientation 轴（尺寸链恒为绝对 H/V）。 */
    const direction = dragged?.branch.fixedDirection
        ?? (dragged?.branch.orientation === 'horizontal'
            ? [1, 0] as const
            : dragged?.branch.orientation === 'vertical'
                ? [0, 1] as const
                : null);

    if (!direction) {
        return null;
    }

    const length = Math.hypot(direction[0], direction[1]);

    if (length < 1e-12) {
        return null;
    }

    /** 链左法向（单位向量）；共享轴偏移 = 被拖标注在法向上的投影。 */
    const normal: readonly [number, number] = [-direction[1] / length, direction[0] / length];
    const axisOffset = draggedLabel[0] * normal[0] + draggedLabel[1] * normal[1];
    const positions = new Map<string, readonly [number, number]>();

    members.forEach((snapshot) => {
        if (snapshot.dimensionId === draggedDimensionId) {
            positions.set(snapshot.dimensionId, draggedLabel);
            return;
        }

        const current = snapshot.display.labelPosition;

        if (!current) {
            return;
        }

        const delta = axisOffset - (current[0] * normal[0] + current[1] * normal[1]);

        positions.set(snapshot.dimensionId, [
            current[0] + normal[0] * delta,
            current[1] + normal[1] * delta,
        ]);
    });

    return positions;
}
